import { zodResolver } from '@hookform/resolvers/zod';
import { type CustomerContactInput, CustomerContactInputSchema } from '@utazzo/shared';
import { UzButton, UzDialog, UzForm, UzFormField, UzInput, UzSelect, UzTextarea } from '@utazzo/ui';
import { useForm } from 'react-hook-form';

import { useAddContact } from '../use-customers';

const SALUTATION_OPTIONS = [
  { value: 'Mr', label: 'Mr' },
  { value: 'Ms', label: 'Ms' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Dr', label: 'Dr' },
];

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
  { value: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say' },
];

const DEFAULTS: CustomerContactInput = {
  relationship: '',
  firstName: '',
};

const ContactFields = ({ pending }: { pending: boolean }): React.JSX.Element => (
  <div className="space-y-3">
    <UzFormField name="relationship">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Relationship"
          placeholder="e.g. Spouse, Parent, Emergency contact"
          required
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="salutation">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Salutation"
          options={SALUTATION_OPTIONS}
          value={(field.value as string) || undefined}
          onValueChange={field.onChange}
          placeholder="Select (optional)"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="firstName">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="First name"
          placeholder="e.g. Raj"
          required
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="lastName">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          label="Last name"
          placeholder="Optional"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="email">
      {({ field, error }) => (
        <UzInput
          {...field}
          type="email"
          value={(field.value as string | null | undefined) ?? ''}
          label="Email"
          placeholder="Optional"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
      <UzFormField name="mobileCountry">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            label="Country code"
            placeholder="91"
            maxLength={4}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <div className="col-span-2">
        <UzFormField name="mobileNumber">
          {({ field, error }) => (
            <UzInput
              {...field}
              value={(field.value as string | null | undefined) ?? ''}
              label="Mobile number"
              placeholder="9876543210"
              error={error}
              disabled={pending}
            />
          )}
        </UzFormField>
      </div>
    </div>
    <UzFormField name="dateOfBirth">
      {({ field, error }) => (
        <UzInput
          {...field}
          type="date"
          value={(field.value as string | null | undefined) ?? ''}
          label="Date of birth"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="gender">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Gender"
          options={GENDER_OPTIONS}
          value={(field.value as string) || undefined}
          onValueChange={field.onChange}
          placeholder="Select (optional)"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="notes">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          label="Notes"
          placeholder="Optional internal notes"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
  </div>
);

interface ContactFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
}

export const ContactFormDialog = ({
  open,
  onOpenChange,
  customerId,
}: ContactFormDialogProps): React.JSX.Element => {
  const addContact = useAddContact();
  const pending = addContact.isPending;

  const form = useForm<CustomerContactInput>({
    resolver: zodResolver(CustomerContactInputSchema),
    defaultValues: DEFAULTS,
  });

  const onSubmit = form.handleSubmit(async (values) => {
    await addContact.mutateAsync({ customerId, payload: values });
    form.reset(DEFAULTS);
    onOpenChange(false);
  });

  return (
    <UzDialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) form.reset(DEFAULTS);
        onOpenChange(isOpen);
      }}
      title="Add contact"
      description="Secondary contacts such as a spouse, parent, or emergency contact."
      size="xl"
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={onSubmit} loading={pending}>
            Save contact
          </UzButton>
        </div>
      }
    >
      <UzForm form={form} onSubmit={onSubmit}>
        <ContactFields pending={pending} />
      </UzForm>
    </UzDialog>
  );
};
